// src/components/NeSyncStatusBanner.tsx
//
// ネクストエンジン原価取得ジョブの「経過」と「結果」を、モーダルではなく
// 商品原価ページ上に表示するバナー。モーダルは取得を始めたら閉じ、進捗と結果は
// この一覧の画面で確認できるようにする(ずっとモーダルを開いたままにしない)。
//
// 文言は IT にくわしくない利用者にも伝わるやさしい言葉にする。結果はモール内の
// 店舗ごとに何件反映できたかまで示す。
// アクセシビリティ: 進捗/完了は role="status"(aria-live=polite)、失敗は role="alert"。

import React, { useEffect, useState } from "react";
import { apiGetJson } from "../utils/apiClient";
import type { NeJob } from "../hooks/useNeJobPolling";

type CompanyShop = { mall: "rakuten" | "amazon"; mallShopId: string; displayName?: string };

const MALL_LABEL: Record<string, string> = { rakuten: "楽天市場", amazon: "Amazon" };
const RUNNING = new Set(["queued", "running"]);

// 'YYYY-MM' / 'YYYY-MM-DD' → '2026年7月'
function formatYm(ym: unknown): string {
    const s = String(ym ?? "");
    const m = s.match(/^(\d{4})-(\d{2})/);
    if (!m) return "";
    return `${m[1]}年${Number(m[2])}月`;
}

// 実行中ジョブの経過テキスト(やさしい言葉)。
function runningText(resultJson: any): string {
    const phase = resultJson?.phase;
    if (phase === "fetching") {
        const fetched = Number(resultJson?.fetched ?? 0).toLocaleString();
        const total =
            resultJson?.reportedCount != null ? Number(resultJson.reportedCount).toLocaleString() : null;
        return total
            ? `ネクストエンジンから商品の情報を読み込んでいます…（${fetched} / ${total}件）`
            : `ネクストエンジンから商品の情報を読み込んでいます…`;
    }
    if (phase === "landing") return "読み込んだ原価を保存しています…";
    if (phase === "fanout") return "各店舗の原価に反映しています…";
    return "原価を取得しています…";
}

type Target = { mall: string; key: string; upserted: number; status: string; message?: string };

export default function NeSyncStatusBanner({
    job,
    pollError,
    onDismiss,
}: {
    job: NeJob | null;
    pollError: string | null;
    onDismiss: () => void;
}) {
    const [shops, setShops] = useState<CompanyShop[]>([]);

    const finished = job && ["succeeded", "partial", "failed", "cancelled"].includes(job.status);
    const showResult = Boolean(finished && job && job.status !== "failed");

    // 結果を店舗名つきで見せるため、完了時に店舗一覧を取得する(名前解決用)。
    useEffect(() => {
        if (!showResult) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiGetJson<{ shops: CompanyShop[] }>("/api/company/shops");
                if (alive) setShops(Array.isArray(res?.shops) ? res.shops : []);
            } catch {
                if (alive) setShops([]);
            }
        })();
        return () => {
            alive = false;
        };
    }, [showResult]);

    if (!job && !pollError) return null;

    const shopName = (mall: string, key: string): string => {
        const hit = shops.find((s) => s.mall === mall && s.mallShopId === key);
        return hit?.displayName || key;
    };

    // 実行中(ポーリング途中のネットワーク揺らぎ等)のエラーは軽く出す。
    if (pollError && (!job || !finished)) {
        return (
            <div className="mb-3">
                <div
                    role="alert"
                    className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                    <p>{pollError}</p>
                    <DismissButton onClick={onDismiss} />
                </div>
            </div>
        );
    }

    if (!job) return null;

    // 受付済み(queued) / 実行中(running)
    // 実行は VPS 側のバッチが順番に行うため、クリック直後は queued の待ち時間がある。
    // queued の間は「受け付けました」を、実行が始まったら進捗を出す。
    if (RUNNING.has(job.status)) {
        const text =
            job.status === "queued"
                ? "原価の取得を受け付けました。順番に反映しています。少しお待ちください。"
                : runningText(job.resultJson);
        return (
            <div className="mb-3">
                <div
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
                >
                    <span
                        className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-300 border-t-blue-700"
                        aria-hidden="true"
                    />
                    <p>{text}</p>
                </div>
            </div>
        );
    }

    // 失敗
    if (job.status === "failed") {
        return (
            <div className="mb-3">
                <div
                    role="alert"
                    className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                            <p className="font-semibold">原価を取得できませんでした。</p>
                            <p>
                                少し時間をおいて、もう一度お試しください。何度も失敗する場合は、
                                「ネクストエンジン連携」から連携をやり直してください。
                            </p>
                            {job.errorSummary && (
                                <p className="text-xs text-red-500">詳細: {job.errorSummary}</p>
                            )}
                        </div>
                        <DismissButton onClick={onDismiss} />
                    </div>
                </div>
            </div>
        );
    }

    // 完了(succeeded / partial)
    const resultJson = job.resultJson ?? {};
    const targets: Target[] = Array.isArray(resultJson.targets) ? resultJson.targets : [];
    const succeeded = targets.filter((t) => t.status === "succeeded");
    const failed = targets.filter((t) => t.status === "failed");
    const warnings: string[] = Array.isArray(resultJson.warnings) ? resultJson.warnings : [];
    const fetched = Number(resultJson.fetched ?? 0).toLocaleString();
    const ym = formatYm(resultJson.yearMonth);

    return (
        <div className="mb-3">
            <div
                role="status"
                aria-live="polite"
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-2">
                        <p className="font-semibold">
                            ネクストエンジンから{fetched}件の商品を読み込み{ym ? `、${ym}の原価に反映しました。` : "ました。"}
                        </p>

                        {succeeded.length > 0 && (
                            <div>
                                <p className="text-emerald-800">反映結果</p>
                                <ul className="mt-0.5 space-y-0.5">
                                    {succeeded.map((t, i) => (
                                        <li key={`ok-${i}`}>
                                            {MALL_LABEL[t.mall] ?? t.mall}／{shopName(t.mall, t.key)}：
                                            <span className="font-medium">{Number(t.upserted || 0).toLocaleString()}件</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {failed.length > 0 && (
                            <div className="text-amber-800">
                                <p>反映できなかった店舗があります</p>
                                <ul className="mt-0.5 space-y-0.5">
                                    {failed.map((t, i) => (
                                        <li key={`ng-${i}`}>
                                            {MALL_LABEL[t.mall] ?? t.mall}／{shopName(t.mall, t.key)}：反映できませんでした
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {warnings.length > 0 && (
                            <ul className="list-disc pl-5 text-amber-800">
                                {warnings.map((w, i) => (
                                    <li key={`w-${i}`}>{w}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <DismissButton onClick={onDismiss} />
                </div>
            </div>
        </div>
    );
}

function DismissButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="このお知らせを閉じる"
            className="shrink-0 rounded p-1 text-current opacity-70 hover:bg-black/5 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
        >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.3 4.9 4.9 6.3 8.6 10l-3.7 3.7 1.4 1.4L10 11.4l3.7 3.7 1.4-1.4L11.4 10l3.7-3.7-1.4-1.4L10 8.6 6.3 4.9z" />
            </svg>
        </button>
    );
}
